import React, { useState, useEffect } from "react";
import { Card, Button, Table, Modal, Form, Alert } from "react-bootstrap";
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  getUploadUrl,
} from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./ClientManagement.css";
import "./Dashboard.css";

const FALLBACK_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="100%" height="100%" fill="%23e9ecef"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%236c757d" font-family="Arial" font-size="12">No Image</text></svg>';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nama: "",
    testimoni: "",
    alamat: "",
    nourut: "",
    logo: null,
    foto: null,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await getClients();
      if (response.data.success) {
        setClients(response.data.data);
      }
    } catch (err) {
      setAlert({
        show: true,
        type: "danger",
        message: "Gagal memuat data client",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nama: "",
      testimoni: "",
      alamat: "",
      nourut: "",
      logo: null,
      foto: null,
    });
    setLogoPreview(null);
    setFotoPreview(null);
  };

  const handleShowModal = (client = null) => {
    if (client) {
      setCurrentClient(client);
      setFormData({
        nama: client.nama || "",
        testimoni: client.testimoni || "",
        alamat: client.alamat || "",
        nourut: client.nourut != null ? String(client.nourut) : "",
        logo: null,
        foto: null,
      });
      setLogoPreview(client.logo ? getUploadUrl(client.logo) : null);
      setFotoPreview(client.foto ? getUploadUrl(client.foto) : null);
    } else {
      setCurrentClient(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentClient(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, [field]: file }));

    const reader = new FileReader();
    reader.onloadend = () => {
      if (field === "logo") setLogoPreview(reader.result);
      else setFotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert({ show: false, type: "", message: "" });

    try {
      const user = getStoredUser();

      const formDataToSend = new FormData();
      formDataToSend.append("nama", formData.nama);
      formDataToSend.append("testimoni", formData.testimoni);
      formDataToSend.append("alamat", formData.alamat);
      formDataToSend.append("nourut", formData.nourut || "");
      formDataToSend.append("userid", user?.userid || "admin");

      if (formData.logo) formDataToSend.append("logo", formData.logo);
      if (formData.foto) formDataToSend.append("foto", formData.foto);

      if (currentClient) {
        await updateClient(currentClient.id, formDataToSend);
        setAlert({
          show: true,
          type: "success",
          message: "Client berhasil diupdate!",
        });
      } else {
        await createClient(formDataToSend);
        setAlert({
          show: true,
          type: "success",
          message: "Client berhasil ditambahkan!",
        });
      }

      handleCloseModal();
      fetchClients();
    } catch (err) {
      setAlert({
        show: true,
        type: "danger",
        message:
          err.response?.data?.message ||
          "Gagal menyimpan client. Silakan coba lagi.",
      });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus client ini?")) {
      return;
    }

    try {
      await deleteClient(id);
      setAlert({
        show: true,
        type: "success",
        message: "Client berhasil dihapus!",
      });
      fetchClients();
    } catch (err) {
      setAlert({
        show: true,
        type: "danger",
        message:
          err.response?.data?.message || "Gagal menghapus client.",
      });
    }
  };

  const truncate = (text, max = 60) => {
    if (!text) return "—";
    return text.length > max ? text.substring(0, max) + "..." : text;
  };

  return (
    <div className="client-management-page">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-handshake"></i>
            </div>
            <div>
              <h1 className="page-header-title">Kelola Client</h1>
              <p className="page-header-subtitle">
                Kelola data client yang ditampilkan di halaman utama website
              </p>
            </div>
          </div>
          <Button
            variant="outline-light"
            className="btn-add"
            onClick={() => handleShowModal()}
          >
            <i className="fas fa-plus me-2"></i>
            Tambah Client
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        >
          {alert.message}
        </Alert>
      )}

      <Card className="table-card">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">
                Belum ada data client. Tambahkan client pertama Anda!
              </p>
            </div>
          ) : (
            <Table responsive hover className="content-table">
              <thead>
                <tr>
                  <th width="5%">ID</th>
                  <th width="10%">Logo</th>
                  <th width="18%">Nama</th>
                  <th width="25%">Testimoni</th>
                  <th width="18%">Alamat</th>
                  <th width="8%">Urutan</th>
                  <th width="10%">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.id}</td>
                    <td>
                      <div className="client-thumb">
                        <img
                          src={
                            client.logo
                              ? getUploadUrl(client.logo)
                              : FALLBACK_IMAGE
                          }
                          alt={client.nama}
                          onError={(e) => {
                            e.target.src = FALLBACK_IMAGE;
                          }}
                        />
                      </div>
                    </td>
                    <td>
                      <strong>{client.nama}</strong>
                    </td>
                    <td>{truncate(client.testimoni, 80)}</td>
                    <td>{truncate(client.alamat, 50)}</td>
                    <td>{client.nourut ?? "—"}</td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleShowModal(client)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentClient ? "Edit Client" : "Tambah Client Baru"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Nama Client <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                placeholder="Contoh: PT Halal Indonesia"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Testimoni Client</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="testimoni"
                value={formData.testimoni}
                onChange={handleInputChange}
                placeholder="Testimoni atau ulasan dari client..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Alamat Client</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="alamat"
                value={formData.alamat}
                onChange={handleInputChange}
                placeholder="Alamat lengkap client..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Urutan Tampil</Form.Label>
              <Form.Control
                type="number"
                name="nourut"
                value={formData.nourut}
                onChange={handleInputChange}
                placeholder="Contoh: 1, 2, 3..."
                min="0"
              />
              <Form.Text className="text-muted">
                Angka lebih kecil akan ditampilkan lebih dulu di halaman depan
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Logo Client</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => handleImageChange("logo", e)}
                  />
                  {logoPreview && (
                    <div className="client-image-preview mt-2">
                      <img src={logoPreview} alt="Preview logo" />
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Foto Client</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => handleImageChange("foto", e)}
                  />
                  {fotoPreview && (
                    <div className="client-image-preview mt-2">
                      <img src={fotoPreview} alt="Preview foto" />
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Batal
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Simpan
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientManagement;
